'use client'

import Image from 'next/image'
import { useState } from 'react'
import type { CSSProperties } from 'react'
import { CarouselContent, CarouselItem, type CarouselApi } from '@/components/ui/carousel'
import { CarouselBlockShell } from '@/components/ui/carousel-block-shell'
import { useCarouselCurrentIndex } from '@/components/ui/use-carousel-current-index'
import { useMarqueeCycleCopies } from '@/components/ui/use-marquee-cycle-copies'
import { cn } from '@/lib/utils'
import { generateHref } from '@/lib/generate-href'
import type { Icon, Media, Partner } from '@/payload-types'

type PartnerCarouselItemData = {
  id: string
  name: string
  href: string | null
  logoUrl: string | null
  logoAlt: string | null
  objectPosition?: string
}

export type PartnerCarouselSize = 'large' | 'small'
export type PartnerCarouselTreatmentType = 'logo' | 'image'
export type PartnerCarouselLogoFit = 'cover' | 'contain'
export type PartnerCarouselLogoPadding = 'none' | 'padding'
export type PartnerCarouselType = 'default' | 'marquee'
export type PartnerCarouselBackground =
  | 'row-1'
  | 'row-2'
  | 'row-3'
  | 'row-4'
  | 'row-5'
  | 'card-bg'
  | 'transparent'

const backgroundClassMap: Record<PartnerCarouselBackground, string> = {
  'row-1': 'bg-theme-row-1',
  'row-2': 'bg-theme-row-2',
  'row-3': 'bg-theme-row-3',
  'row-4': 'bg-theme-row-4',
  'row-5': 'bg-theme-row-5',
  'card-bg': 'bg-theme-card-background',
  transparent: '',
}

function getObjectPosition(media: Media): string | undefined {
  const focalX = typeof media.focalX === 'number' ? media.focalX : 50
  const focalY = typeof media.focalY === 'number' ? media.focalY : 50

  if (typeof media.focalX !== 'number' && typeof media.focalY !== 'number') {
    return undefined
  }

  const clampedX = Math.min(100, Math.max(0, focalX))
  const clampedY = Math.min(100, Math.max(0, focalY))

  return `${clampedX}% ${clampedY}%`
}

function getPartnerItemData(partner: Partner): PartnerCarouselItemData {
  const media = partner.logo && typeof partner.logo !== 'string' ? (partner.logo as Media) : null
  const size = media?.sizes?.medium || media?.sizes?.large
  const logoUrl = size?.url || media?.url || null

  return {
    id: partner.id,
    name: partner.name,
    href: partner.link && typeof partner.link !== 'string' ? generateHref(partner.link) || null : null,
    logoUrl,
    logoAlt: media?.alt || partner.name,
    objectPosition: media ? getObjectPosition(media) : undefined,
  }
}

function normalizeMaxHeight(maxHeight: number | null | undefined): number | undefined {
  if (typeof maxHeight !== 'number' || !Number.isFinite(maxHeight) || maxHeight <= 0) return undefined
  return Math.floor(maxHeight)
}

function getSecondsPerItem(speed: number | null | undefined, isSmall: boolean) {
  if (typeof speed === 'number' && Number.isFinite(speed) && speed > 0) return speed
  return isSmall ? 7 : 9
}

export function PartnerCarouselClient({
  partners,
  hidePartnersWithNoImage = false,
  size = 'small',
  treatmentType = 'logo',
  logoFit = 'contain',
  logoPadding = 'padding',
  carouselType = 'default',
  speed,
  stopOnHover = true,
  background = 'card-bg',
  maxHeight,
  iconLeft,
  iconRight,
}: {
  partners: Partner[]
  hidePartnersWithNoImage?: boolean | null
  size?: PartnerCarouselSize | null
  treatmentType?: PartnerCarouselTreatmentType | null
  logoFit?: PartnerCarouselLogoFit | null
  logoPadding?: PartnerCarouselLogoPadding | null
  carouselType?: PartnerCarouselType | null
  speed?: number | null
  stopOnHover?: boolean | null
  background?: PartnerCarouselBackground | null
  maxHeight?: number | null
  iconLeft?: (string | null) | Icon
  iconRight?: (string | null) | Icon
}) {
  const [api, setApi] = useState<CarouselApi>()
  const partnerItems = partners
    .map(getPartnerItemData)
    .filter((partnerItem) => !hidePartnersWithNoImage || partnerItem.logoUrl)
  const count = partnerItems.length
  const current = useCarouselCurrentIndex(api, count)
  const {
    containerRef: marqueeContainerRef,
    cycleCopies: marqueeCycleCopies,
    cycleRef: marqueeCycleRef,
  } = useMarqueeCycleCopies({
    enabled: carouselType === 'marquee',
    itemCount: count,
  })

  if (!partnerItems.length) {
    return null
  }

  const renderedPartnerItems = count === 3 ? [...partnerItems, ...partnerItems] : partnerItems
  const resolvedSize = size === 'large' ? 'large' : 'small'
  const isSmall = resolvedSize === 'small'
  const slideBasisClass = isSmall
    ? cn(
        'basis-[62%] sm:basis-[42%]',
        count <= 2
          ? 'md:basis-[34%] lg:basis-[26%]'
          : count === 3
            ? 'md:basis-[28%] lg:basis-[22%]'
            : 'md:basis-[24%] lg:basis-[18%]',
      )
    : cn(
        'basis-[85%] sm:basis-[75%]',
        count <= 2
          ? 'md:basis-[70%] lg:basis-[65%]'
          : count === 3
            ? 'basis-3/4 md:basis-1/2 lg:basis-[35%]'
            : 'md:basis-1/2 lg:basis-1/3',
      )
  const imageSizes = isSmall
    ? '(max-width: 640px) 62vw, (max-width: 768px) 42vw, (max-width: 1024px) 28vw, 260px'
    : '(max-width: 640px) 85vw, (max-width: 768px) 75vw, (max-width: 1024px) 50vw, 600px'
  const logoFitClass = logoFit === 'cover' ? 'object-cover' : 'object-contain'
  const isImageTreatment = treatmentType === 'image'
  const hasLogoPadding = logoPadding !== 'none'
  const isTransparent = background === 'transparent'
  const backgroundClass = backgroundClassMap[background ?? 'card-bg']
  const resolvedMaxHeight = normalizeMaxHeight(maxHeight)
  const marqueeItems = Array.from({ length: marqueeCycleCopies }).flatMap(() => partnerItems)
  const marqueeItemClass = isSmall
    ? 'w-[220px] md:w-[240px] lg:w-[260px]'
    : 'w-[85vw] sm:w-[75vw] md:w-[50vw] lg:w-[600px]'
  const marqueeDuration = `${marqueeItems.length * getSecondsPerItem(speed, isSmall)}s`
  const slideGutterClass = isSmall ? 'pl-4 md:pl-5 lg:pl-6' : 'pl-4 md:pl-6'
  const trackGutterClass = isSmall ? '-ml-4 md:-ml-5 lg:-ml-6 py-2' : '-ml-4 md:-ml-6 py-2'
  const itemMaxSize = isSmall ? 260 : 600
  const itemStyle: CSSProperties | undefined = resolvedMaxHeight
    ? {
        maxHeight: `${resolvedMaxHeight}px`,
        maxWidth: `min(${itemMaxSize}px, ${resolvedMaxHeight}px)`,
      }
    : undefined

  function renderLogoCard(
    partnerItem: PartnerCarouselItemData,
    isCurrent: boolean,
    disableLink = false,
    removeTopMargin = false,
  ) {
    const card = (
      <article
        style={itemStyle}
        className={cn(
          'mx-auto mb-6 aspect-square w-full overflow-hidden transition-all',
          !isTransparent && 'theme-card border',
          !removeTopMargin && 'mt-4',
          isSmall ? 'max-w-[220px] md:max-w-[240px] lg:max-w-[260px]' : 'max-w-[80vw] md:max-w-125',
          !isCurrent && 'origin-center scale-95',
          !isImageTreatment && hasLogoPadding && 'p-5',
          !isTransparent && backgroundClass,
          !partnerItem.logoUrl && 'flex items-center justify-center text-center',
        )}
      >
        {partnerItem.logoUrl ? (
          <>
            <div className="relative h-full w-full">
              <Image
                src={partnerItem.logoUrl}
                alt={partnerItem.logoAlt || partnerItem.name}
                fill
                sizes={imageSizes}
                className={cn(
                  'h-full w-full',
                  isImageTreatment ? 'object-cover' : cn('brightness-0', logoFitClass),
                )}
                style={{ objectPosition: isImageTreatment ? partnerItem.objectPosition : undefined }}
              />
            </div>
            <span className="sr-only">{partnerItem.name}</span>
          </>
        ) : (
          <p>{partnerItem.name}</p>
        )}
      </article>
    )

    return partnerItem.href && !disableLink ? (
      <a href={partnerItem.href} className="no-underline" target="_blank" rel="noopener noreferrer">
        {card}
      </a>
    ) : (
      card
    )
  }

  if (carouselType === 'marquee') {
    return (
      <section aria-label="Partners carousel" className="w-screen overflow-hidden">
        <div ref={marqueeContainerRef} className="group flex overflow-hidden">
          <div
            className={cn(
              'flex w-max animate-image-carousel-marquee motion-reduce:animate-none',
              stopOnHover !== false && 'group-hover:[animation-play-state:paused]',
            )}
            style={{
              '--image-carousel-marquee-duration': marqueeDuration,
            } as CSSProperties}
          >
            {[0, 1].map((groupIndex) => (
              <div
                key={groupIndex}
                ref={groupIndex === 0 ? marqueeCycleRef : undefined}
                className="flex shrink-0"
                aria-hidden={groupIndex > 0}
              >
                {marqueeItems.map((partnerItem, i) => (
                  <div
                    key={`${partnerItem.id}-${groupIndex}-${i}`}
                    className={cn('shrink-0 px-3', marqueeItemClass)}
                  >
                    {renderLogoCard(partnerItem, true, groupIndex > 0, true)}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>
    )
  }

  return (
    <CarouselBlockShell
      ariaLabel="Partners carousel"
      setApi={setApi}
      opts={{ align: 'center', containScroll: false, loop: count > 1 }}
      iconLeft={iconLeft}
      iconRight={iconRight}
      count={count}
      current={current}
      onSelect={(index) => api?.scrollTo(index)}
    >
      <CarouselContent className={trackGutterClass}>
        {renderedPartnerItems.map((partnerItem, i) => {
          const normalizedIndex = i % count

          return (
            <CarouselItem
              key={`${partnerItem.id}-${i}`}
              className={cn(slideBasisClass, slideGutterClass)}
            >
              {renderLogoCard(partnerItem, normalizedIndex === current)}
            </CarouselItem>
          )
        })}
      </CarouselContent>
    </CarouselBlockShell>
  )
}
